import { useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Auto-rotating image carousel for Material Library cards & modal.
 *
 * Props:
 *   images:        [{url, caption}]  — list of gallery images (relative URLs OK)
 *   intervalMs:    auto-advance interval (default 4000). Pass 0 to disable.
 *   showDots:      bool — render the dot indicator (default true if >1 image)
 *   showCaption:   bool — render the caption strip overlay (default false)
 *   className:     wrapper className
 *   imgClassName:  className for the <img>
 *   ariaLabel:     accessible name for the image
 *   pauseOnHover:  bool — pause auto-advance on hover (default true)
 *
 * Auto-advance is also paused when the document is hidden (no work in background tabs).
 * When only one image is supplied, this is just a passthrough <img>.
 */
const MaterialCarousel = ({
  images = [],
  intervalMs = 4000,
  showDots = true,
  showCaption = false,
  className = "",
  imgClassName = "",
  ariaLabel = "",
  pauseOnHover = true,
}) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Reset on image-list change.
  useEffect(() => { setIdx(0); }, [images.length, images.map((i) => i?.url || "").join("|")]);

  // Auto-advance loop. Skips when paused/hidden/single-image/intervalMs<=0.
  useEffect(() => {
    if (intervalMs <= 0 || images.length <= 1 || paused) return undefined;
    const tick = () => {
      if (document.hidden) return;
      setIdx((p) => (p + 1) % images.length);
    };
    timerRef.current = setInterval(tick, intervalMs);
    const onVis = () => {
      // Reset the timer when the user comes back so the first slide gets its
      // full reveal window instead of advancing instantly.
      if (!document.hidden) {
        clearInterval(timerRef.current);
        timerRef.current = setInterval(tick, intervalMs);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [intervalMs, images.length, paused]);

  if (!images || images.length === 0) return null;
  const current = images[idx] || images[0];
  const src = current.url?.startsWith("http") ? current.url : `${BACKEND}${current.url || ""}`;

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => pauseOnHover && setPaused(true)}
      onMouseLeave={() => pauseOnHover && setPaused(false)}
      data-testid="material-carousel"
    >
      <img
        key={idx}
        src={src}
        alt={current.caption || ariaLabel || ""}
        className={`transition-opacity duration-500 ${imgClassName}`}
        loading="lazy"
        style={{ animation: "carousel-fade 700ms ease-out" }}
        data-testid={`material-carousel-img-${idx}`}
      />
      {showCaption && current.caption && (
        <div
          className="absolute left-2 right-2 bottom-2 px-2.5 py-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] rounded-sm pointer-events-none"
          style={{ background: "rgba(28,25,23,0.85)", color: "#FFB500" }}
        >
          {current.caption}
        </div>
      )}
      {showDots && images.length > 1 && (
        <div
          className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-auto"
          data-testid="material-carousel-dots"
        >
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={(e) => { e.stopPropagation(); setIdx(i); }}
              aria-label={`Photo ${i + 1} of ${images.length}`}
              aria-current={i === idx ? "true" : "false"}
              className="w-2 h-2 rounded-full transition-all"
              style={{
                background: i === idx ? "#FFB500" : "rgba(255,255,255,0.55)",
                width: i === idx ? 16 : 8,
                boxShadow: "0 0 0 1px rgba(28,25,23,0.4)",
              }}
              data-testid={`material-carousel-dot-${i}`}
            />
          ))}
        </div>
      )}
      <style>{`
        @keyframes carousel-fade {
          from { opacity: 0; transform: scale(1.02); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default MaterialCarousel;
